/**
 * Utilitaires pour la gestion de l'authentification (cookies httpOnly côté API).
 */
import axios from 'axios';
import { clearSession, getSessionItem } from '@/utils/storage';
import { clearBrowserCachesAndServiceWorkers } from '@/utils/browserCache';
import {
  clearAuthenticatedSession,
  clearUserProfileSession,
  isAuthenticatedSession,
  purgeLegacyJwtStorage,
  getUserRoleFromSession,
} from '@/utils/sessionAuth';
import { getVerifiedRole } from '@/utils/verifiedProfile';

export const TOKEN_KEYS = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
  ROLE: 'user_role',
};

export const clearUserData = () => {
  clearUserProfileSession();
  purgeLegacyJwtStorage();
  clearAuthenticatedSession();
  clearSession();
};

export const logout = (
  router,
  message = null,
  showAlert = false,
  forceReload = true,
  queryParams = null
) => {
  const doRedirect = () => {
    let connexionPath = '/connexion';
    if (queryParams && typeof queryParams === 'object') {
      const qs = new URLSearchParams(queryParams).toString();
      if (qs) connexionPath = `/connexion?${qs}`;
    }
    if (showAlert && message) {
      // Ne pas utiliser alert() : message technique ou intrusif pour l'utilisateur.
      // Le bandeau de message sur la page de connexion fournit un meilleur retour.
      console.info('Logout notice:', message);
    }
    if (forceReload) {
      clearBrowserCachesAndServiceWorkers()
        .catch(() => {})
        .finally(() => {
          if (router) {
            router.replace(connexionPath).catch(() => {});
          }
          window.location.replace(connexionPath);
        });
      return;
    }
    if (router) {
      router.push(connexionPath).catch(() => {});
    } else {
      window.location.href = connexionPath;
    }
  };

  axios
    .post('/api/logout/', {}, {
      withCredentials: true,
      timeout: 10000,
      __skipSessionRecovery: true,
    })
    .catch(() => {})
    .finally(() => {
      clearUserData();
      doRedirect();
    });
};

export const isAuthenticated = () => isAuthenticatedSession();

export const getUserRole = () => {
  try {
    const verified = getVerifiedRole();
    if (verified) return verified;
    const role = getUserRoleFromSession();
    return role || null;
  } catch {
    return null;
  }
};

/** Profils « client » applicatif (client ou user en base). */
export function isClientFacingRole(role) {
  const r = String(role ?? getUserRole() ?? '')
    .trim()
    .toLowerCase();
  return r === 'client' || r === 'user';
}

/** Recharge le profil serveur (rôle, permissions, user_id en session). */
export async function refreshProfileFromApi() {
  const { syncEmployeProfileFromApi } = await import('@/utils/permissions');
  await syncEmployeProfileFromApi();
  return getUserRole();
}

/** @deprecated JWT non exposé au JS — auth par cookies httpOnly */
export const getAccessToken = () => null;
